import { useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';

const REFRESH_INTERVAL_MS = 30 * 60 * 1000;  // every 30 min
const WARN_BEFORE_MS = 30 * 1000;            // 30-sec warning toast
const POSTPONE_MS = 5 * 60 * 1000;           // "Postpone" delays by 5 min

// Periodic cache-flush + page-reload watchdog. Every 30 minutes the user
// sees a warning toast for 30 seconds with a "Postpone" button (defers by
// 5 min); if not postponed, the app clears the HTTP/SW cache and reloads.
//
// localStorage is intentionally preserved so the user stays signed in
// across the auto-refresh — this is a freshness mechanism, not a logout.
// (Idle-logout is handled separately by IdleLogoutManager.)
export default function AutoRefreshManager() {
  const tickTimer = useRef(null);
  const warnToastId = useRef(null);

  const doRefresh = useCallback(async () => {
    try {
      // Clear all SW caches (network-first SW will repopulate on next load).
      if (typeof caches !== 'undefined') {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (e) { /* ignore — we'll still hard-reload */ }
    // Hard reload — bypasses HTTP cache where supported. Some browsers
    // ignore the deprecated `true` argument; the cache flush above is the
    // real freshness guarantee.
    window.location.reload();
  }, []);

  const scheduleNextTick = useCallback((delay = REFRESH_INTERVAL_MS) => {
    if (tickTimer.current) clearTimeout(tickTimer.current);
    tickTimer.current = setTimeout(() => {
      // Show the warning first.
      if (warnToastId.current) toast.dismiss(warnToastId.current);
      warnToastId.current = toast(
        'Refreshing the app for the latest content in 30 seconds…',
        {
          duration: WARN_BEFORE_MS,
          action: {
            label: 'Postpone 5 min',
            onClick: () => {
              if (warnToastId.current) toast.dismiss(warnToastId.current);
              scheduleNextTick(POSTPONE_MS);
            },
          },
          icon: '🔄',
        },
      );
      // Then refresh after the warning window.
      tickTimer.current = setTimeout(doRefresh, WARN_BEFORE_MS);
    }, delay);
  }, [doRefresh]);

  useEffect(() => {
    scheduleNextTick();
    return () => {
      if (tickTimer.current) clearTimeout(tickTimer.current);
      if (warnToastId.current) toast.dismiss(warnToastId.current);
    };
  }, [scheduleNextTick]);

  return null;
}
